import { isBrowserOnlyAppUrl } from "modules/deepLinks/browserOnlyUrl";

export type LinkPractice = { platformId: string; platformDomain: string };

/** What the classifier needs to know about the patient: the practices whose hosts are this app. */
export type LinkContext = { practices: LinkPractice[] };

export type ClassifiedLink =
  | { kind: "in-app"; path: string; platformId: string | null }
  | { kind: "external"; url: string }
  /** `tel:` and `mailto:` — handed to the operating system, not a browser. */
  | { kind: "native"; url: string }
  | { kind: "rejected"; reason: "unsupported-scheme" | "api-path" | "unparseable" };

const PLATFORM_ID_PARAM = "platformId";
const SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i;
const NATIVE_SCHEMES = ["tel:", "mailto:"];
const WEB_SCHEMES = ["http:", "https:"];
const API_PATH_PREFIX = "/api/";

/** Whether `link` is an app path (`/shop`) rather than a protocol-relative `//host` URL. */
export const isRelativeAppPath = (link: string): boolean =>
  link.startsWith("/") && !link.startsWith("//");

const PLACEHOLDER_ORIGIN = "https://placeholder.invalid";

const inAppFromPath = (pathWithQuery: string, practiceId: string | null): ClassifiedLink => {
  const { pathname, search, hash } = new URL(pathWithQuery, PLACEHOLDER_ORIGIN);

  if (pathname.startsWith(API_PATH_PREFIX)) {
    return { kind: "rejected", reason: "api-path" };
  }

  const existingPlatformId = new URLSearchParams(search).get(PLATFORM_ID_PARAM);
  const platformId = existingPlatformId ?? practiceId;
  const addsPlatformId = !existingPlatformId && practiceId !== null;
  const query = addsPlatformId
    ? `${search ? `${search}&` : "?"}${PLATFORM_ID_PARAM}=${encodeURIComponent(practiceId)}`
    : search;

  return { kind: "in-app", path: `${pathname}${query}${hash}`, platformId };
};

const classifyWebUrl = (url: URL, context: LinkContext): ClassifiedLink => {
  if (isBrowserOnlyAppUrl(url)) {
    return { kind: "external", url: url.toString() };
  }

  const practice = context.practices.find(
    (candidate) => candidate.platformDomain.toLowerCase() === url.hostname.toLowerCase(),
  );

  if (!practice) {
    return { kind: "external", url: url.toString() };
  }

  return inAppFromPath(`${url.pathname}${url.search}${url.hash}`, practice.platformId);
};

/**
 * Decides what tapping a link inside a message does. Only http(s) links to a host the patient holds
 * a practice for open inside the app (carrying that practice's id so the target loads against its
 * host); every other web link is external. `javascript:`, `data:`, `file:` and other schemes are
 * rejected rather than opened.
 */
export const classifyLink = (link: string, context: LinkContext): ClassifiedLink => {
  const trimmed = link.trim();

  if (isRelativeAppPath(trimmed)) {
    return inAppFromPath(trimmed, null);
  }

  const withScheme = SCHEME_PATTERN.test(trimmed)
    ? trimmed
    : `https://${trimmed.replace(/^\/+/, "")}`;

  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return { kind: "rejected", reason: "unparseable" };
  }

  if (NATIVE_SCHEMES.includes(url.protocol)) {
    return { kind: "native", url: url.toString() };
  }

  if (!WEB_SCHEMES.includes(url.protocol)) {
    return { kind: "rejected", reason: "unsupported-scheme" };
  }

  return classifyWebUrl(url, context);
};
